import moment from 'moment';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import ComponentCircleLoader from '../../../components/Loaders/ComponentCircleLoader';
import SingleDatePicker from '../../../components/SingleDatePicker/SingleDatePicker';
import {
    CommonFlex,
    FilterLabel,
    InputWithLabelSection,
    PageTitle,
} from '../../../styledComponents/common';

const PageHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
    flex-wrap: wrap;
    gap: 12px;
`;

const SectionCard = styled.div`
    background: #fff;
    border-radius: 12px;
    padding: 24px;
    margin-bottom: 24px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.07);
`;

const SectionHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 20px;
`;

const SectionAccent = styled.div`
    wid  th: 5px;
    height: 26px;
    border-radius: 3px;
    background: ${({ color }) => color || '#19AC65'};
`;

const SectionTitle = styled.h3`
    font-size: 16px;
    font-weight: 700;
    color: #2f394e;
    margin: 0;
`;

const CardsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
    @media (max-width: 1200px) {
        grid-template-columns: repeat(3, 1fr);
    }
    @media (max-width: 768px) {
        grid-template-columns: repeat(2, 1fr);
    }
    @media (max-width: 480px) {
        grid-template-columns: 1fr;
    }
`;

const ColoredStatCard = styled.div`
    background: ${({ bg }) => bg || '#f0fdf4'};
    border: 1.5px solid ${({ border }) => border || '#bbf7d0'};
    border-radius: 12px;
    padding: 20px 16px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    min-height: 180px;
    transition: transform 0.15s ease, box-shadow 0.15s ease;
    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 14px ${({ shadow }) => shadow || 'rgba(25,172,101,0.18)'};
    }
`;

const CardIcon = styled.div`
    font-size: 26px;
    margin-bottom: 8px;
    line-height: 1;
`;

const CardValue = styled.div`
    font-size: 36px;
    font-weight: 700;
    color: ${({ color }) => color || '#19AC65'};
    line-height: 1.1;
`;

const CardFraction = styled.span`
    font-size: 18px;
    font-weight: 500;
    color: ${({ color }) => color || '#6b7280'};
`;

const CardLabel = styled.div`
    font-size: 13px;
    font-weight: 600;
    color: #4b5563;
    margin-top: 6px;
`;

// card theme configs
const MEAL_CARDS = [
    {
        key: 'total',
        labelKey: 'total_received_meals',
        // icon: '🍽️',
        bg: '#f0fdf4',
        border: '#86efac',
        shadow: 'rgba(25,172,101,0.18)',
        valueColor: '#16a34a',
    },
    {
        key: 'breakfast',
        labelKey: 'total_received_breakfast',
        // icon: '🌅',
        bg: '#fffbeb',
        border: '#fcd34d',
        shadow: 'rgba(245,158,11,0.18)',
        valueColor: '#d97706',
    },
    {
        key: 'lunch',
        labelKey: 'total_received_lunch',
        // icon: '☀️',
        bg: '#eff6ff',
        border: '#93c5fd',
        shadow: 'rgba(59,130,246,0.18)',
        valueColor: '#2563eb',
    },
    {
        key: 'dinner',
        labelKey: 'total_received_dinner',
        // icon: '🌙',
        bg: '#faf5ff',
        border: '#c4b5fd',
        shadow: 'rgba(139,92,246,0.18)',
        valueColor: '#7c3aed',
    },
];

const LOCATION_CARDS = [
    {
        key: 'received_breakfast',
        labelKey: 'locations_received_breakfast',
        // icon: '🌅',
        bg: '#fffbeb',
        border: '#fcd34d',
        shadow: 'rgba(245,158,11,0.18)',
        valueColor: '#d97706',
    },
    {
        key: 'received_lunch',
        labelKey: 'locations_received_lunch',
        // icon: '☀️',
        bg: '#eff6ff',
        border: '#93c5fd',
        shadow: 'rgba(59,130,246,0.18)',
        valueColor: '#2563eb',
    },
    {
        key: 'received_dinner',
        labelKey: 'locations_received_dinner',
        // icon: '🌙',
        bg: '#faf5ff',
        border: '#c4b5fd',
        shadow: 'rgba(139,92,246,0.18)',
        valueColor: '#7c3aed',
    },
];

const CateringDashboard = ({ getCateringDashboardSummary, summary, isLoading }) => {
    const { t } = useTranslation();
    const [selectedDate, setSelectedDate] = useState(moment());

    useEffect(() => {
        getCateringDashboardSummary({ date: selectedDate.format('YYYY-MM-DD') });
    }, []);

    const handleDateChange = date => {
        setSelectedDate(date);
        getCateringDashboardSummary({ date: date.format('YYYY-MM-DD') });
    };

    const meals = summary?.meals || {};
    const locations = summary?.catering_locations || {};
    const totalLocations = locations.total || 0;

    return (
        <div className="content">
            <PageHeader>
                <PageTitle style={{ marginBottom: 0 }}>{t('catering_dashboard')}</PageTitle>
                <InputWithLabelSection>
                    {/* <FilterLabel>{t('date')}</FilterLabel> */}
                    {/* <SingleDatePicker
                        id="catering-dashboard-date"
                        date={selectedDate}
                        onDateChange={handleDateChange}
                        dateDisplayFormat="DD/MM/YYYY"
                        placeholder={t('select_date')}
                        disableFutureDates
                    /> */}
                </InputWithLabelSection>
            </PageHeader>

            {isLoading ? (
                <ComponentCircleLoader padding="10vh" />
            ) : (
                <>
                    {/* Meals Section */}
                    <SectionCard>
                        <SectionHeader>
                            <SectionAccent color="#19AC65" />
                            <SectionTitle>{t('meals_summary')}</SectionTitle>
                        </SectionHeader>
                        <CardsGrid>
                            {MEAL_CARDS.map(card => (
                                <ColoredStatCard
                                    key={card.key}
                                    bg={card.bg}
                                    border={card.border}
                                    shadow={card.shadow}
                                >
                                    {/* <CardIcon>{card.icon}</CardIcon> */}
                                    <CardValue color={card.valueColor}>
                                        {meals[card.key] ?? '-'}
                                    </CardValue>
                                    <CardLabel>{t(card.labelKey)}</CardLabel>
                                </ColoredStatCard>
                            ))}
                        </CardsGrid>
                    </SectionCard>

                    {/* Locations Section */}
                    <SectionCard>
                        <SectionHeader>
                            <SectionAccent color="#3b82f6" />
                            <SectionTitle>{t('locations_summary')}</SectionTitle>
                        </SectionHeader>
                        <CardsGrid>
                            {LOCATION_CARDS.map(card => (
                                <ColoredStatCard
                                    key={card.key}
                                    bg={card.bg}
                                    border={card.border}
                                    shadow={card.shadow}
                                >
                                    <CardIcon>{card.icon}</CardIcon>
                                    <CardValue color={card.valueColor}>
                                        {locations[card.key] ?? '-'}
                                        <CardFraction color={card.valueColor + '99'}>
                                            {' '}/ {totalLocations}
                                        </CardFraction>
                                    </CardValue>
                                    <CardLabel>{t(card.labelKey)}</CardLabel>
                                </ColoredStatCard>
                            ))}
                        </CardsGrid>
                    </SectionCard>
                </>
            )}
        </div>
    );
};

export default CateringDashboard;
